/**
 * useResolvedDocument Hook
 *
 * Simple read-only hook for fetching resolved document data.
 * Use this for rendering contexts where mutations aren't needed.
 */

import { useQuery } from '@tanstack/react-query'
import type { ResolvedDocument } from '@/types/blocks'
import { secureFetch } from '@/utils/SecureFetch'

/**
 * Fetches resolved document data from the API
 */
async function fetchResolvedDocument(
	documentId: string
): Promise<{ resolvedDocument: ResolvedDocument; playbookId: string }> {
	const response = await secureFetch(`/api/documents/${documentId}/resolved`)

	if (!response.ok) {
		throw new Error(`Failed to fetch document: ${response.status}`)
	}

	return response.json()
}

/**
 * Query key for resolved document data
 */
export const getResolvedDocumentQueryKey = (documentId: string | null) =>
	['resolvedDocument', documentId] as const

/**
 * Simple read-only hook for fetching resolved document data.
 *
 * @example
 * ```tsx
 * function DocumentVisual({ block }) {
 *   const { document, isLoading } = useResolvedDocument(block.id)
 *   // Render document...
 * }
 * ```
 */
export function useResolvedDocument(documentId: string | null, enabled = true) {
	const shouldFetch = documentId !== null && enabled

	const {
		data,
		isLoading,
		error,
	} = useQuery({
		queryKey: getResolvedDocumentQueryKey(documentId),
		queryFn: () => fetchResolvedDocument(documentId!),
		enabled: shouldFetch,
		// Cache for 30 seconds to reduce unnecessary refetches
		staleTime: 30000,
		// Don't refetch on window focus for static rendering
		refetchOnWindowFocus: false,
	})

	return {
		document: data?.resolvedDocument ?? null,
		playbookId: data?.playbookId ?? null,
		isLoading: shouldFetch ? isLoading : false,
		error: error instanceof Error ? error : null,
	}
}
